/**
 * Duplicate Portfolio Component (Wrapper)
 *
 * This is a minimal wrapper around the generic DuplicateEntity component
 * that handles portfolio-specific mutation logic.
 *
 * @author Housefolios
 * @created 2025-01-27
 * @modified 2025-08-12
 */

import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client/react'
import { enqueueSnackbar } from 'notistack'
import DuplicateEntityComponent from './index.jsx'
import { PORTFOLIO_CONFIG } from './entityConfigs'
import { COPY_PORTFOLIO } from '@/housefolios-components/Portfolio/mutations'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { cloneDeep, unset } from '@/utils/lodash/index.js'

/**
 * Duplicate Portfolio Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.portfolio - The portfolio object to duplicate
 * @param {Array} props.portfolios - Available portfolios
 * @param {string} props.selectedPortfolio - Currently selected portfolio
 * @param {Function} props.setSelected - Function to clear selection after duplication
 * @param {Object} props.session - User session object
 * @param {Function} props.onSuccess - Success callback
 * @param {Function} props.onError - Error callback
 * @param {boolean} props.notifications - Whether to show as notification menu item
 * @param {boolean} props.rightClickMenu - Whether to show as right-click menu item
 * @param {boolean} props.sidebarMenu - Whether to show in sidebar menu
 * @param {string} props.buttonText - Custom button text
 */
function DuplicatePortfolioComponent(props) {
  const {
    portfolio,
    portfolios,
    selectedPortfolio: initialSelectedPortfolio,
    setSelected,
    session,
    onSuccess,
    onError,
    notifications = false,
    rightClickMenu = false,
    sidebarMenu = false,
    buttonText = '',
    ...otherProps
  } = props

  // GraphQL mutation
  const [copyPortfolio] = useMutation(COPY_PORTFOLIO)

  // Handle form submission
  const handleSubmit = async (additionalState = {}) => {
    try {
      let portfolioInput = {
        name: additionalState?.name,
        strategy: additionalState?.strategy,
        description: additionalState?.description,
      }
      if (additionalState.copyAssumptions) {
        portfolioInput.assumptions = cloneDeep(portfolio?.assumptions)
        unset(portfolioInput.assumptions, '_id')
        unset(portfolioInput.assumptions, '__typename')
      }
      const result = await copyPortfolio({
        awaitRefetchQueries: true,
        variables: {
          portfolioId: portfolio._id,
          portfolioInput: portfolioInput, // Pass additional fields
        },
        refetchQueries: [
          { query: GET_PORTFOLIOS },
          { query: GET_ME },
        ],
      })

      // Success handling
      enqueueSnackbar('Portfolio was duplicated successfully', {
        variant: 'success',
        autoHideDuration: 3000,
      })

      if (setSelected) setSelected([])
      if (onSuccess) onSuccess(result)
    } catch (error) {
      // Error handling
      console.error('Failed to duplicate portfolio:', error)
      enqueueSnackbar('Portfolio failed to duplicate', {
        variant: 'error',
        autoHideDuration: 3000,
      })

      if (onError) onError(error)
    }
  }

  const { limits = {} } = session.me
  const overLimit = portfolios.length >= limits.portfolioLimit

  return (
    <DuplicateEntityComponent
      {...otherProps}
      entityType="portfolio"
      entityId={portfolio._id}
      entity={portfolio}
      portfolios={portfolios}
      selectedPortfolio={null} // Portfolios don't need destination portfolio
      setSelected={setSelected}
      session={session}
      config={PORTFOLIO_CONFIG}
      onSuccess={onSuccess}
      onError={onError}
      onSubmit={handleSubmit}
      notifications={notifications}
      rightClickMenu={rightClickMenu}
      sidebarMenu={sidebarMenu}
      underwriting={false} // Portfolios don't have underwriting context
      buttonText={buttonText}
      overLimit={overLimit}
    />
  )
}

// PropTypes - including rendering props
DuplicatePortfolioComponent.propTypes = {
  portfolio: PropTypes.object.isRequired,
  portfolios: PropTypes.array.isRequired,
  selectedPortfolio: PropTypes.string,
  setSelected: PropTypes.func,
  session: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  notifications: PropTypes.bool,
  rightClickMenu: PropTypes.bool,
  sidebarMenu: PropTypes.bool,
  buttonText: PropTypes.string,
}

export default DuplicatePortfolioComponent
